import { create } from 'zustand'
import type { ReactNode } from 'react'
import type { Product } from '@/types'

type ModalType =
  | 'checkout'
  | 'customer-search'
  | 'customer-details'
  | 'product-details'
  | 'manager-override'
  | 'confirm'
  | 'cash-drawer'
  | 'void-receipt'
  | 'hold-receipt'
  | 'held-receipts'
  | 'age-verify'
  | 'lot-select'
  | 'discount'
  | 'payout'
  | 'gift-card-sell'
  | 'notes'
  | 'barcode-match'
  | null

export interface ProductModalRequest {
  product: Product
  // Defaults to true. Set false when the open shouldn't add/bump the
  // product in the Recent Products panel (e.g. clicks from Recent itself).
  trackRecent?: boolean
}

interface ConfirmModalOptions {
  title: string
  // A plain string is rendered as a pre-line paragraph (the common case).
  // Pass a node when the dialog needs real structure -- e.g. the checkout
  // ID check, which centers the age and highlights the cutoff date in a
  // badge the cashier can read at a glance while holding an ID.
  message: ReactNode
  confirmLabel?: string
  // Pass `null` to hide the Cancel button (single-button "OK" warning
  // mode). Backdrop click / X still dismiss via hideConfirm.
  cancelLabel?: string | null
  onConfirm: () => void
  onCancel?: () => void
  variant?: 'default' | 'danger'
}

interface UIState {
  // Modal state
  activeModal: ModalType
  modalData: Record<string, unknown>

  // Cross-component request to open the ProductModal. The route owns
  // the actual modal mount (`/routes/pos/index.tsx`); other components
  // (CommandPalette, OrderDetailModal, etc.) push a Product through
  // here and the route's effect mirrors it into local state.
  // `trackRecent: false` lets the LeftSidebar Recent panel reopen a
  // product without bumping it to the top of the recent list.
  productModalRequest: ProductModalRequest | null

  // Confirm modal
  confirmOptions: ConfirmModalOptions | null

  // Command palette
  isCommandPaletteOpen: boolean

  // Cash In modal — coordinated open state so both user-triggered
  // (FunctionsTab "Cash In" button) and enforcement-triggered (POSLayout
  // auto-prompt when no clock-in exists) opens use the same mount.
  // The actual <CashInModal /> is rendered once in POSLayout.
  isCashInModalOpen: boolean

  // Cash Out modal — same coordination pattern as Cash In: mounted
  // once in POSLayout, opened via this store from the FunctionsTab
  // "Cash Out" button.
  isCashOutModalOpen: boolean

  // Clerk re-PIN overlay (ClerkPinReauthModal, mounted once in
  // POSLayout). Non-null = raised, holding the recovery reason from the
  // API client's clerk-session recovery (client.ts
  // recoverDeadClerkSession). The overlay is deliberately NOT part of
  // activeModal: it must sit above every other modal and cannot be
  // displaced by normal modal opens.
  clerkReauthRequired: string | null

  // Quick Actions sheet — mobile bottom sheet launched from the 20% action
  // button on the cart/scan bar. Mounted once in POSLayout.
  isQuickActionsOpen: boolean

  // Sidebar collapse states
  isLeftSidebarCollapsed: boolean
  isRightSidebarCollapsed: boolean
  
  // Loading states
  isProcessingPayment: boolean
  isProcessingAction: boolean
  
  // Toast/notification
  toasts: Toast[]
}

interface Toast {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  duration?: number
}

interface UIActions {
  // Modal actions
  openModal: (modal: ModalType, data?: Record<string, unknown>) => void
  closeModal: () => void

  // ProductModal cross-component plumbing
  requestProductModal: (product: Product, options?: { trackRecent?: boolean }) => void
  clearProductModalRequest: () => void

  // Confirm modal helper
  showConfirm: (options: ConfirmModalOptions) => void
  hideConfirm: () => void
  
  // Command palette
  toggleCommandPalette: () => void
  setCommandPaletteOpen: (open: boolean) => void

  // Cash In modal
  openCashInModal: () => void
  closeCashInModal: () => void

  // Cash Out modal
  openCashOutModal: () => void
  closeCashOutModal: () => void

  // Clerk re-PIN overlay
  requireClerkReauth: (reason: string) => void
  clearClerkReauth: () => void

  // Quick Actions sheet (mobile)
  openQuickActions: () => void
  closeQuickActions: () => void

  // Reset both cash-modal flags at a clerk-session boundary. These flags
  // are in-memory and otherwise survive the logout→PIN→login cycle on
  // terminals that don't hard-reload, which would re-show a previous
  // clerk's Cash Out the moment the next clerk lands on /pos. Called on
  // clerk login (useClerkLogin) and clerk-session teardown (clearClerkAuth).
  closeCashModals: () => void

  // Sidebar toggles
  toggleLeftSidebar: () => void
  toggleRightSidebar: () => void
  
  // Loading states
  setProcessingPayment: (processing: boolean) => void
  setProcessingAction: (processing: boolean) => void
  
  // Toast actions
  showToast: (toast: Omit<Toast, 'id'>) => void
  dismissToast: (id: string) => void
  clearToasts: () => void
}

type UIStore = UIState & UIActions

// Helper to generate toast ID
const generateToastId = () => `toast_${Date.now()}`

export const useUIStore = create<UIStore>((set, get) => ({
  // Initial state
  activeModal: null,
  modalData: {},
  productModalRequest: null,
  confirmOptions: null,
  isCommandPaletteOpen: false,
  isCashInModalOpen: false,
  isCashOutModalOpen: false,
  clerkReauthRequired: null,
  isQuickActionsOpen: false,
  isLeftSidebarCollapsed: false,
  isRightSidebarCollapsed: false,
  isProcessingPayment: false,
  isProcessingAction: false,
  toasts: [],
  
  // Modal actions
  openModal: (modal, data = {}) => {
    set({ activeModal: modal, modalData: data })
  },
  
  closeModal: () => {
    set({ activeModal: null, modalData: {} })
  },

  requestProductModal: (product, options) => {
    set({
      productModalRequest: {
        product,
        trackRecent: options?.trackRecent ?? true,
      },
    })
  },

  clearProductModalRequest: () => {
    set({ productModalRequest: null })
  },
  
  // Confirm modal — runs as an independent layer above any other open
  // modal. We intentionally do NOT touch `activeModal` here: showing a
  // confirm on top of (e.g.) the CheckoutModal must not unmount the
  // checkout, otherwise canceling the confirm leaves the cashier
  // dropped back to the products view with their cart input lost.
  // ConfirmModal is rendered based on `confirmOptions` presence in the
  // route, so it stacks naturally over the active modal.
  showConfirm: (options) => {
    set({ confirmOptions: options })
  },

  hideConfirm: () => {
    const { confirmOptions } = get()
    // Skip onCancel in single-button "acknowledge-only" mode
    // (caller signaled by passing `cancelLabel: null`). Otherwise the
    // X / backdrop dismiss path silently fires destructive cancel
    // logic the caller meant to suppress -- e.g. an "acknowledge this
    // warning" dialog whose onCancel rolls back state. The Cancel
    // button is hidden in that mode, but the X and backdrop still
    // route here, so the gate lives at the callback level.
    if (confirmOptions?.cancelLabel !== null) {
      confirmOptions?.onCancel?.()
    }
    set({ confirmOptions: null })
  },
  
  // Command palette
  toggleCommandPalette: () => {
    set(state => ({ isCommandPaletteOpen: !state.isCommandPaletteOpen }))
  },
  
  setCommandPaletteOpen: (open) => {
    set({ isCommandPaletteOpen: open })
  },

  // Cash In modal
  requireClerkReauth: (reason) => {
    set({ clerkReauthRequired: reason })
  },

  clearClerkReauth: () => {
    set({ clerkReauthRequired: null })
  },

  openCashInModal: () => {
    set({ isCashInModalOpen: true })
  },

  closeCashInModal: () => {
    set({ isCashInModalOpen: false })
  },

  // Cash Out modal
  openCashOutModal: () => {
    set({ isCashOutModalOpen: true })
  },

  closeCashOutModal: () => {
    set({ isCashOutModalOpen: false })
  },

  openQuickActions: () => {
    set({ isQuickActionsOpen: true })
  },

  closeQuickActions: () => {
    set({ isQuickActionsOpen: false })
  },

  closeCashModals: () => {
    set({ isCashInModalOpen: false, isCashOutModalOpen: false })
  },

  // Sidebar toggles
  toggleLeftSidebar: () => {
    set(state => ({ isLeftSidebarCollapsed: !state.isLeftSidebarCollapsed }))
  },
  
  toggleRightSidebar: () => {
    set(state => ({ isRightSidebarCollapsed: !state.isRightSidebarCollapsed }))
  },
  
  // Loading states
  setProcessingPayment: (processing) => {
    set({ isProcessingPayment: processing })
  },
  
  setProcessingAction: (processing) => {
    set({ isProcessingAction: processing })
  },
  
  // Toast actions
  showToast: (toast) => {
    const id = generateToastId()
    const newToast = { ...toast, id }
    
    set(state => ({ toasts: [...state.toasts, newToast] }))
    
    // Auto-dismiss after duration (default 5 seconds)
    const duration = toast.duration ?? 5000
    if (duration > 0) {
      setTimeout(() => {
        get().dismissToast(id)
      }, duration)
    }
  },
  
  dismissToast: (id) => {
    set(state => ({ toasts: state.toasts.filter(t => t.id !== id) }))
  },
  
  clearToasts: () => {
    set({ toasts: [] })
  },
}))
